// The Profile tab (route /more). Owner restructure round 2 (2026-08-10):
// everything the user adjusts often lives HERE — goals, reminders, units,
// app preferences, and the admin tools — one tap from the tab bar. The
// gear → Settings keeps only account plumbing, legal, sign-out, delete.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Settings as SettingsIcon, Sparkles, Check, Link2, Target, Gem,
  Bell, Ruler, SlidersHorizontal, Users, Repeat2, RotateCcw,
} from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { restorePurchases } from "../lib/purchases";
import { useEntitlement } from "../lib/useEntitlement";
import { Paywall } from "../components/Paywall";
import { Browser } from "@capacitor/browser";
import { useToast } from "../lib/toast";
import { useMe } from "../lib/useMe";
import { SectionHeader } from "../components/ui/SectionHeader";
import { SettingsGroup, SettingsLink, SettingsButton, IconChip } from "../components/ui/SettingsList";
import { isNativeApp } from "../lib/native";
import { getReturnTicket, setReturnTicket } from "../lib/adminSwitch";
import { clearPending } from "../lib/pendingOnboarding";
import { ADMIN_SWITCH_EMAILS } from "@shared/admin-switch";

export function More(_props: { onLogout?: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const { me } = useMe();
  const { entitlement, premium, billingEnabled } = useEntitlement();
  const [showPaywall, setShowPaywall] = useState(false);
  const [restoring, setRestoring] = useState(false);

  async function manageSubscription() {
    if (entitlement?.source === "apple") {
      await Browser.open({ url: "https://apps.apple.com/account/subscriptions" });
    } else if (entitlement?.source === "stripe") {
      try {
        const { url } = await api.stripePortal();
        window.location.href = url;
      } catch { toast.error(t("common.failed")); }
    }
  }

  async function tapRestore() {
    setRestoring(true);
    try {
      const r = await restorePurchases();
      if (r === "premium") toast.success(t("paywall.restored"));
      else if (r === "none") toast.error(t("paywall.nothing_to_restore"));
      else toast.error(t("common.failed"));
    } finally { setRestoring(false); }
  }

  const fullName = me ? [me.name, me.last_name].filter(Boolean).join(" ") : "";
  const isAdmin = me?.role === "admin";

  // TEMPORARY admin switcher (owner request, to be removed): switch between
  // greezy (admin) and luca.a666a to test premium vs free without logging out.
  const returnTicket = getReturnTicket();
  const otherEmail = ADMIN_SWITCH_EMAILS.find((e) => e !== me?.email);
  async function switchTo(email: string) {
    try {
      const { return_ticket } = await api.adminSwitch(email);
      setReturnTicket(return_ticket);
      clearPending();
      window.location.replace("/more");
    } catch { toast.error(t("toast.failed")); }
  }
  async function resetOtherOnboarding() {
    if (!otherEmail) return;
    try {
      await api.adminResetOnboarding(otherEmail);
      toast.success(t("admin.reset_done", { email: otherEmail }));
    } catch { toast.error(t("toast.failed")); }
  }
  async function switchBack() {
    const tk = getReturnTicket();
    if (!tk) return;
    try {
      await api.switchBack(tk);
      setReturnTicket(null);
      clearPending();
      window.location.replace("/more");
    } catch { toast.error(t("toast.failed")); }
  }

  return (
    <div className="space-y-5 pt-4 animate-rise">
      <div className="flex items-center justify-between">
        <h1 className="text-title font-bold tracking-tight">{t("tab.more")}</h1>
        <button
          onClick={() => navigate("/more/settings")}
          aria-label={t("settings.title")}
          className="w-11 h-11 flex items-center justify-center text-fg-secondary press"
        >
          <SettingsIcon size={22} />
        </button>
      </div>

      {/* Identity card → My Profile — stays on top (owner): the profile page
          opens with the person, then their subscription. */}
      {me && (
        <button
          onClick={() => navigate("/more/profile")}
          className="w-full flex items-center gap-4 px-4 py-4 glass rounded-2xl press text-left"
        >
          <div className="p-[2.5px] rounded-full bg-gradient-to-br from-brand to-accent-move shrink-0">
            <div className="w-14 h-14 rounded-full bg-elevated flex items-center justify-center text-xl font-semibold">
              {(me.name ?? "").trim().slice(0, 1).toUpperCase() || "?"}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-lg font-semibold truncate">{fullName || me.name}</div>
            {me.username && <div className="text-sm text-fg-secondary truncate">@{me.username}</div>}
            {me.email && <div className="text-sm text-fg-muted truncate">{me.email}</div>}
          </div>
          <span className="text-fg-faint">›</span>
        </button>
      )}

      {/* caloly Premium — first SECTION, right under the identity card: the
          Pro tab left the bar, so status + the way into /pro live here. */}
      {billingEnabled && (
        <section className="space-y-2">
          <SectionHeader>{t("paywall.section")}</SectionHeader>
          <SettingsGroup>
            {premium ? (
              <>
                <div className="flex items-center gap-3 px-4 py-3.5">
                  <IconChip icon={Sparkles} tint="#67d491" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">{t("paywall.active")}</div>
                    {entitlement?.expires_at && (
                      <div className="text-sm text-fg-muted">
                        {t(entitlement.will_renew ? "paywall.renews_on" : "paywall.ends_on", {
                          date: new Date(entitlement.expires_at * 1000).toLocaleDateString(),
                        })}
                      </div>
                    )}
                  </div>
                </div>
                <SettingsLink to="/pro" icon={Gem} tint="#7c5cff" label={t("more.premium_details")} />
                <SettingsButton onClick={manageSubscription} icon={Link2} tint="#64748b" label={t("paywall.manage")} />
              </>
            ) : (
              <>
                <SettingsButton
                  onClick={() => (isNativeApp() ? setShowPaywall(true) : navigate("/upgrade"))}
                  icon={Sparkles}
                  tint="#67d491"
                  label={t("paywall.upgrade_row")}
                  value={t("paywall.trial_badge")}
                />
                <SettingsLink to="/pro" icon={Gem} tint="#7c5cff" label={t("more.premium_details")} />
                {isNativeApp() && (
                  <SettingsButton
                    onClick={tapRestore}
                    icon={Check}
                    tint="#64748b"
                    label={restoring ? t("common.loading") : t("paywall.restore")}
                  />
                )}
              </>
            )}
          </SettingsGroup>
        </section>
      )}

      {/* Goals belong to the person, so they stay on the profile. */}
      <section className="space-y-2">
        <SectionHeader>{t("settings.coach")}</SectionHeader>
        <SettingsGroup>
          <SettingsLink to="/more/goals" icon={Target} tint="#f43f5e" label={t("more.goals_targets")} />
        </SettingsGroup>
      </section>

      {/* Frequently-adjusted preferences, one group (iOS pattern: related
          single links share a card instead of a section each). */}
      <section className="space-y-2">
        <SectionHeader>{t("settings.preferences")}</SectionHeader>
        <SettingsGroup>
          <SettingsLink to="/more/reminders" icon={Bell} tint="#f59e0b" label={t("settings.reminders_section")} />
          <SettingsLink to="/more/units" icon={Ruler} tint="#10b981" label={t("units.title")} />
          <SettingsLink to="/more/preferences" icon={SlidersHorizontal} tint="#3b82f6" label={t("settings.app_prefs")} />
        </SettingsGroup>
      </section>

      {/* TEMPORARY: return to the admin account while impersonating (the
          impersonated user is not admin, so the block below won't render). */}
      {returnTicket && !isAdmin && (
        <section className="space-y-2">
          <SectionHeader>{t("settings.admin")}</SectionHeader>
          <SettingsGroup>
            <SettingsButton onClick={switchBack} icon={Repeat2} tint="#f97316" label={t("admin.return")} />
          </SettingsGroup>
        </section>
      )}

      {/* Admin — the owner's daily tools; they live on the profile, not
          buried in Settings. */}
      {isAdmin && (
        <section className="space-y-2">
          <SectionHeader>{t("settings.admin")}</SectionHeader>
          <SettingsGroup>
            <SettingsLink to="/more/users" icon={Users} tint="#f97316" label={t("more.users")} />
            {otherEmail && (
              <SettingsButton
                onClick={() => switchTo(otherEmail)}
                icon={Repeat2}
                tint="#f97316"
                label={t("admin.switch_to", { email: otherEmail })}
              />
            )}
            {otherEmail && (
              <SettingsButton
                onClick={resetOtherOnboarding}
                icon={RotateCcw}
                tint="#f97316"
                label={t("admin.reset_onboarding", { email: otherEmail })}
              />
            )}
          </SettingsGroup>
        </section>
      )}

      {showPaywall && <Paywall trigger="settings" onClose={() => setShowPaywall(false)} />}
    </div>
  );
}
